import React, { useState } from 'react';
import { Descriptions, Button, Tag, Space, App, Grid } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { trpc } from '../../lib/trpc';
import CompanyEditForm from './CompanyEditForm';
import type { Company } from '../../types/company';
import BalancedDescriptions from '../common/BalancedDescriptions';
import { KATAKANA_REGEX, PHONE_NUMBER_REGEX, POSTAL_CODE_REGEX } from '@/constants/validation';
import { logger } from '../../utils/logger';
import { SUBSCRIPTION_PLAN_CONFIGS, type ManagedPlanCode } from '@shared/config/subscription-plans';


const legacyPlanLabels: Record<string, string> = {
  free: 'フリー',
  standard: 'スタンダード',
  premium: 'プレミアム',
  enterprise: 'エンタープライズ',
};

const isManagedPlanCode = (plan: string): plan is ManagedPlanCode =>
  Object.prototype.hasOwnProperty.call(SUBSCRIPTION_PLAN_CONFIGS, plan);

const katakanaField = z
  .string()
  .max(200, '200文字以内で入力してください')
  .regex(KATAKANA_REGEX, 'カタカナ（全角）で入力してください')
  .or(z.literal(''));

const phoneField = z
  .string()
  .max(20, '電話番号は20文字以内で入力してください')
  .regex(PHONE_NUMBER_REGEX, '有効な電話番号を入力してください')
  .or(z.literal(''));

// 最低限必要なフィールドのみのスキーマ
const companyUpdateSchema = z.object({
  // 基本情報（必須）
  name: z.string().min(1, '会社名は必須です'),

  // 基本情報（任意、nullableも許容）
  nameKana: katakanaField.nullable().optional(),
  industryId: z.string().uuid('有効な業界を選択してください').nullable().optional(),

  // 住所情報（全て任意、nullableも許容）
  postalCode: z
    .string()
    .regex(POSTAL_CODE_REGEX, '有効な郵便番号を入力してください')
    .or(z.literal(''))
    .nullable()
    .optional(),
  prefecture: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  building: z.string().nullable().optional(),

  // 連絡先情報（全て任意、nullableも許容）
  phone: phoneField.nullable().optional(),
  email: z.string().email('有効なメールアドレスを入力してください').or(z.literal('')).nullable().optional(),
}).passthrough(); // 他のフィールドも許可（送信時に除外される）

type CompanyUpdateInput = z.infer<typeof companyUpdateSchema>;

interface CompanyInfoTabProps {
  company?: Company;
  isManager: boolean;
}

const CompanyInfoTab: React.FC<CompanyInfoTabProps> = ({ company, isManager }) => {
  const { message } = App.useApp();
  const [isEditing, setIsEditing] = useState(false);
  const utils = trpc.useUtils();
  const screens = Grid.useBreakpoint();
  const columnCount = screens.lg || screens.xl || screens.xxl ? 2 : 1;
  const fullSpan = columnCount;

  // 業界情報を取得
  const { data: industry } = trpc.industry.getAll.useQuery(undefined, {
    select: (industries) => industries.find((i) => i.id === company?.industryId),
  });

  const updateMutation = trpc.company.update.useMutation({
    onSuccess: () => {
      message.success('会社情報を更新しました');
      setIsEditing(false);
      // 会社情報のキャッシュを無効化
      utils.company.getCurrentCompany.invalidate();
    },
    onError: (error) => {
      message.error(`更新に失敗しました: ${error.message}`);
    },
  });

  const form = useForm<CompanyUpdateInput>({
    resolver: zodResolver(companyUpdateSchema),
    defaultValues: (company || {}) as CompanyUpdateInput,
  });

  // 編集モードに入る時にフォームをリセット
  const startEditing = () => {
    form.reset((company || {}) as CompanyUpdateInput);
    setIsEditing(true);
  };

  const handleSave = async (data: CompanyUpdateInput) => {
    if (!company) return;

    // 送信するフィールドのみ抽出（定義されたフィールドのみ）
    const updateData: Partial<CompanyUpdateInput> = {
      name: data.name,
      nameKana: data.nameKana || null,
      industryId: data.industryId || null,
      postalCode: data.postalCode || null,
      prefecture: data.prefecture || null,
      city: data.city || null,
      address: data.address || null,
      building: data.building || null,
      phone: data.phone || null,
      email: data.email || null,
    };

    logger.info('Saving company data:', updateData);

    try {
      await updateMutation.mutateAsync({
        id: company.id,
        data: updateData,
      });
    } catch (error) {
      logger.error('Save error:', error);
    }
  };

  const handleCancel = () => {
    form.reset((company || {}) as CompanyUpdateInput);
    setIsEditing(false);
  };

  const getSubscriptionStatusTag = (status?: string) => {
    const statusMap: Record<string, { color: string; label: string }> = {
      trialing: { color: 'blue', label: '無料登録中' },
      active: { color: 'green', label: '有効' },
      past_due: { color: 'orange', label: '支払い遅延' },
      canceled: { color: 'red', label: 'キャンセル済み' },
      suspended: { color: 'gray', label: '停止中' },
    };

    const config = statusMap[status || ''] || { color: 'default', label: '不明' };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const getSubscriptionPlanLabel = (plan?: string) => {
    if (!plan) {
      return '不明';
    }

    if (isManagedPlanCode(plan)) {
      return SUBSCRIPTION_PLAN_CONFIGS[plan].label;
    }

    return legacyPlanLabels[plan] ?? '不明';
  };

  if (!company) {
    return <div>会社情報を読み込み中...</div>;
  }

  const handleValidationError = (errors: FieldErrors<CompanyUpdateInput>) => {
    logger.error('バリデーションエラー:', errors);

    // エラーメッセージを構築
    const errorMessages: string[] = [];
    Object.keys(errors).forEach((key) => {
      const error = errors[key];
      if (error?.message) {
        errorMessages.push(`${getFieldLabel(key)}: ${error.message}`);
      }
    });

    if (errorMessages.length > 0) {
      message.error({
        content: (
          <div>
            <div style={{ marginBottom: '8px' }}>入力内容に誤りがあります：</div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {errorMessages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        ),
        duration: 5,
      });
    } else {
      message.error('入力内容に誤りがあります。フォームを確認してください。');
    }
  };

  // フィールド名を日本語に変換
  const getFieldLabel = (fieldName: string): string => {
    const labels: Record<string, string> = {
      name: '会社名',
      nameKana: '会社名（カナ）',
      industryId: '業界',
      postalCode: '郵便番号',
      prefecture: '都道府県',
      city: '市区町村',
      address: '住所',
      building: '建物名',
      phone: '電話番号',
      email: 'メールアドレス',
    };
    return labels[fieldName] || fieldName;
  };

  if (isEditing) {
    return (
      <div>
        <div className="mb-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold">会社情報編集</h3>
          <Space>
            <Button
              icon={<SaveOutlined />}
              type="primary"
              onClick={form.handleSubmit(handleSave, handleValidationError)}
              loading={updateMutation.isPending}
            >
              保存
            </Button>
            <Button
              icon={<CloseOutlined />}
              onClick={handleCancel}
            >
              キャンセル
            </Button>
          </Space>
        </div>
        <CompanyEditForm form={form} showAdminFields={false} />
      </div>
    );
  }

  return (
    <div>
      {isManager && (
        <div className="mb-4 flex justify-end">
          <Button
            icon={<EditOutlined />}
            onClick={startEditing}
          >
            編集
          </Button>
        </div>
      )}

      <BalancedDescriptions bordered column={columnCount}>
        {/* 基本情報 */}
        <Descriptions.Item label="会社名">{company.name}</Descriptions.Item>
        <Descriptions.Item label="会社名（カナ）">{company.nameKana || '-'}</Descriptions.Item>

        <Descriptions.Item label="業界">
          {industry ? (
            <Tag color="blue">
              {industry.name}
              {industry.nameEn && ` (${industry.nameEn})`}
            </Tag>
          ) : (
            company.industry || '-'
          )}
        </Descriptions.Item>

        {/* 連絡先情報 */}
        <Descriptions.Item label="電話番号">{company.phone || '-'}</Descriptions.Item>
        <Descriptions.Item label="メールアドレス" span={fullSpan}>{company.email || '-'}</Descriptions.Item>

        {/* 住所情報 */}
        <Descriptions.Item label="郵便番号">{company.postalCode || '-'}</Descriptions.Item>
        <Descriptions.Item label="都道府県">{company.prefecture || '-'}</Descriptions.Item>
        <Descriptions.Item label="市区町村">{company.city || '-'}</Descriptions.Item>
        <Descriptions.Item label="住所">{company.address || '-'}</Descriptions.Item>
        <Descriptions.Item label="建物名" span={fullSpan}>{company.building || '-'}</Descriptions.Item>

        {/* 契約情報 */}
        <Descriptions.Item label="契約プラン">
          <Space direction="vertical" size={0}>
            <Tag>{getSubscriptionPlanLabel(company.subscriptionPlan)}</Tag>
            {company.scheduledPlanCode && company.scheduledPlanEffectiveDate && (
              <span style={{ fontSize: '12px', color: '#1890ff', marginTop: '4px' }}>
                → {new Date(company.scheduledPlanEffectiveDate).toLocaleDateString('ja-JP')} から{' '}
                <Tag color="blue" style={{ fontSize: '11px' }}>
                  {getSubscriptionPlanLabel(company.scheduledPlanCode)}
                </Tag>
                に変更予定
              </span>
            )}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="ステータス">
          {getSubscriptionStatusTag(company.subscriptionStatus)}
        </Descriptions.Item>
      </BalancedDescriptions>
    </div>
  );
};

export default CompanyInfoTab;
